'use client';

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';

import { usePrefiereMovimientoReducido } from '@/components/asistente/movimientoReducido';
import { ID_TODAS, categoriaDeConsulta, type PestanaPortada } from '@/lib/blog/portada-blog';
import { cn } from '@/lib/utils';

/** Una tarjeta ya renderizada en el servidor y la pestaña en la que se ve (`null`: solo «Todo»). */
export interface TarjetaFiltrable {
    slug: string;
    categoria: string | null;
    nodo: ReactNode;
}

interface PropiedadesFiltroCategoriasBlog {
    pestanas: PestanaPortada[];
    tarjetas: TarjetaFiltrable[];
    destacado: ReactNode | null;
    categoriaInicial: string;
}

const ID_PANEL = 'panel-blog';
const CATEGORIAS_CON_DESTACADO = new Set([ID_TODAS, 'notas-clinicas']);
// Margen que deja el chip elegido respecto al borde de la fila móvil (UI Considerations).
const MARGEN_CHIP = 16;

function idPestana(id: string): string {
    return `pestana-blog-${id}`;
}

function seVe(categoria: string | null, seleccion: string): boolean {
    return seleccion === ID_TODAS || categoria === seleccion;
}

function indiceDestino(tecla: string, actual: number, total: number): number | null {
    if (tecla === 'ArrowRight') return (actual + 1) % total;
    if (tecla === 'ArrowLeft') return (actual - 1 + total) % total;
    if (tecla === 'Home') return 0;
    if (tecla === 'End') return total - 1;
    return null;
}

// En escritorio la fila no desplaza y esto no hace nada; jsdom y navegadores viejos no traen
// `scrollTo` en elementos. Es una llamada al DOM, no estado: no provoca renders.
function desplazarHastaPestana(fila: HTMLElement | null, boton: HTMLElement | undefined, suave: boolean) {
    if (!fila || !boton || typeof fila.scrollTo !== 'function') return;
    const delta = boton.getBoundingClientRect().left - fila.getBoundingClientRect().left - MARGEN_CHIP;
    fila.scrollTo({ left: fila.scrollLeft + delta, behavior: suave ? 'smooth' : 'auto' });
}

interface PropiedadesPestana {
    pestana: PestanaPortada;
    activa: boolean;
    alElegir: (id: string) => void;
    registrar: (id: string, nodo: HTMLButtonElement | null) => void;
}

function PestanaCategoria({ pestana, activa, alElegir, registrar }: PropiedadesPestana) {
    return (
        <button
            type="button"
            role="tab"
            id={idPestana(pestana.id)}
            ref={(nodo) => registrar(pestana.id, nodo)}
            aria-selected={activa}
            aria-controls={ID_PANEL}
            tabIndex={activa ? 0 : -1}
            onClick={() => alElegir(pestana.id)}
            className={cn(
                'h-11 shrink-0 whitespace-nowrap rounded-full border-2 border-ink px-ds-4 text-ds-200 text-ink',
                activa ? 'bg-accent-blue font-bold shadow-ds-chip' : 'bg-surface-2 font-semibold hover:border-ink/40',
            )}
        >
            {pestana.etiqueta} <span className="font-semibold text-on-surface-variant">{pestana.cuenta}</span>
        </button>
    );
}

function useBotonesPestana() {
    const botones = useRef(new Map<string, HTMLButtonElement>());
    const registrar = (id: string, nodo: HTMLButtonElement | null) => {
        if (nodo) botones.current.set(id, nodo);
        else botones.current.delete(id);
    };
    return { botones, registrar };
}

// El chip elegido se lleva a la vista en la fila móvil, suave salvo con movimiento reducido.
function useElegirPestana(categoriaInicial: string) {
    const [seleccion, setSeleccion] = useState(() => categoriaInicial);
    const fila = useRef<HTMLDivElement>(null);
    const { botones, registrar } = useBotonesPestana();
    const reducido = usePrefiereMovimientoReducido();
    const elegir = (id: string, enfocar = false) => {
        setSeleccion(id);
        if (enfocar) botones.current.get(id)?.focus();
        desplazarHastaPestana(fila.current, botones.current.get(id), !reducido);
    };
    // Si la pestaña llega elegida desde la URL, en móvil puede quedar fuera de la fila visible.
    useEffect(() => {
        if (categoriaInicial !== ID_TODAS) desplazarHastaPestana(fila.current, botones.current.get(categoriaInicial), false);
    }, [categoriaInicial, botones]);
    return { seleccion, fila, registrar, elegir };
}

function useSeleccionPestanas(pestanas: PestanaPortada[], categoriaInicial: string) {
    const { seleccion, fila, registrar, elegir } = useElegirPestana(categoriaInicial);
    const alPulsarTecla = (evento: KeyboardEvent<HTMLDivElement>) => {
        const actual = pestanas.findIndex((pestana) => pestana.id === seleccion);
        const destino = indiceDestino(evento.key, actual, pestanas.length);
        if (destino === null) return;
        evento.preventDefault();
        elegir(pestanas[destino].id, true);
    };
    return { seleccion, fila, registrar, elegir, alPulsarTecla };
}

interface PropiedadesPanel {
    tarjetas: TarjetaFiltrable[];
    seleccion: string;
}

function PanelTarjetas({ tarjetas, seleccion }: PropiedadesPanel) {
    return (
        <div
            role="tabpanel"
            id={ID_PANEL}
            aria-labelledby={idPestana(seleccion)}
            className="grid grid-cols-1 gap-[18px] px-4 lg:grid-cols-3 lg:px-8 xl:px-20"
        >
            {tarjetas.map((tarjeta) => (
                <div key={tarjeta.slug} hidden={!seVe(tarjeta.categoria, seleccion)}>
                    {tarjeta.nodo}
                </div>
            ))}
        </div>
    );
}

/**
 * Pestañas de Blog_Portada (UI-SPEC §Interaction 8). Todas las tarjetas llegan renderizadas del
 * servidor y siguen en el HTML sea cual sea la pestaña (SEO): elegir una solo pone `hidden` a las
 * que no tocan, al instante y sin pedir nada a la red. Teclado del patrón WAI-ARIA con activación
 * automática: las flechas eligen y mueven el foco a la vez.
 */
export default function FiltroCategoriasBlog({
    pestanas,
    tarjetas,
    destacado,
    categoriaInicial,
}: PropiedadesFiltroCategoriasBlog) {
    const { seleccion, fila, registrar, elegir, alPulsarTecla } = useSeleccionPestanas(pestanas, categoriaInicial);
    return (
        <>
            <div
                ref={fila}
                role="tablist"
                aria-label="Categorías"
                onKeyDown={alPulsarTecla}
                className="no-scrollbar flex min-h-12 items-start gap-ds-2 overflow-x-auto px-4 pb-5 lg:flex-wrap lg:overflow-visible lg:px-8 lg:pb-7 xl:px-20"
            >
                {pestanas.map((pestana) => (
                    <PestanaCategoria
                        key={pestana.id}
                        pestana={pestana}
                        activa={pestana.id === seleccion}
                        alElegir={elegir}
                        registrar={registrar}
                    />
                ))}
            </div>
            {destacado && <div hidden={!CATEGORIAS_CON_DESTACADO.has(seleccion)}>{destacado}</div>}
            <PanelTarjetas tarjetas={tarjetas} seleccion={seleccion} />
        </>
    );
}

/**
 * La misma isla con la pestaña que pide `?categoria=`. Leer la consulta en una ruta estática hace
 * que Next sirva el fallback del Suspense que la envuelve, así que ese fallback es la portada en
 * «Todo»; aquí la consulta solo fija el valor inicial del estado, nunca se copia en un efecto.
 */
export function FiltroCategoriasConConsulta(props: Omit<PropiedadesFiltroCategoriasBlog, 'categoriaInicial'>) {
    const consulta = useSearchParams();
    const categoriaInicial = categoriaDeConsulta(consulta?.get('categoria'), props.pestanas);
    return <FiltroCategoriasBlog {...props} categoriaInicial={categoriaInicial} />;
}
